// Swap WordDTO fields so existing challenge code can be reused for a
// Vietnamese-learning player without modification. When the player is
// learning Vietnamese, we want every challenge to treat the Vietnamese word
// as "the target" — audio prompts speak Vietnamese, image-to-word picks
// Vietnamese spellings, etc. — and English becomes the helper translation.
//
// The cleanest way to do this without duplicating every challenge component
// is to flip the fields at config-build time: english <-> vietnamese, then
// clear the English MP3 URLs so playback falls back to browser TTS in
// vi-VN (since we don't ship Vietnamese MP3 files yet).

import type { WordDTO } from "@shared/types";

/** Slug a piece of Vietnamese text into a URL-safe filename. Mirrors the
 *  slug() helper in scripts/generate-tts-vi.mjs so the client and the
 *  generation script agree on filenames. */
export function viSlug(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    // strip combining diacritics
    .replace(/[̀-ͯ]/g, "")
    // đ doesn't decompose under NFD so handle it explicitly
    .replace(/đ/g, "d")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** URL of the pre-rendered Vietnamese MP3 for a given Vietnamese phrase.
 *  Returns null for empty input so callers can fall back to TTS. */
export function viAudioUrl(text: string | null | undefined): string | null {
  if (!text) return null;
  const slug = viSlug(text);
  return slug ? `/audio/tts/vi/${slug}.mp3` : null;
}

function vietnameseSentenceFor(viWord: string): string {
  return `Đây là ${viWord}.`;
}

export function applyLearnLanguage(words: WordDTO[], learnLanguage: "en" | "vi" | undefined): WordDTO[] {
  if (learnLanguage !== "vi") return words;
  return words.map((w) => {
    const viTarget = w.vietnamese; // becomes the "english" target after swap
    const sentence = vietnameseSentenceFor(viTarget);
    return {
      ...w,
      english: viTarget,            // target word the player must learn
      vietnamese: w.english,        // helper translation shown under the target
      // Point at the pre-rendered Mistral MP3 for this Vietnamese word.
      // resolveLocalAudioUrl in local-assets.ts returns null if the file
      // isn't on disk yet, which makes the audio system fall back to
      // browser TTS in vi-VN — so the game stays playable before the
      // generation script has been run.
      audioUrl: `/audio/tts/vi/${viSlug(viTarget)}.mp3`,
      sentenceAudioUrl: `/audio/tts/vi/${viSlug(sentence)}.mp3`,
      // Synthesized Vietnamese context sentence for the sentence-gap levels.
      sentence,
    };
  });
}
